/**
 * Where the client fetches its Draco and Basis (KTX2) decoders from: our own origin
 * (docs/ops/cdn-static-offload.md §5 S6, story q-cdn-s6-self-host-decoders).
 *
 * Until S6 the loader pulled them from `www.gstatic.com` (Draco) and `cdn.jsdelivr.net` (Basis):
 * two third parties that received every player's IP address and user agent on the first
 * compressed pack, and that the privacy notice never named. The files are the ones `three`
 * itself ships under `examples/jsm/libs/`. The Vite plugin `frontend/build/selfHostDecoders.ts`
 * serves them in dev and emits them into the build at exactly these paths, so nothing is copied
 * into git and a `three` upgrade cannot leave a stale copy behind: the plugin fails the build if
 * the installed `three` is not `THREE_VERSION`.
 *
 * The directory carries the `three` version, so the URL changes whenever the bytes can. They stay
 * on the game origin even when the art comes from the asset CDN: they are code, not art.
 *
 * Pure data, no imports: shared by the browser loader and the (Node-side) Vite plugin.
 */

/** The installed `three` version (node_modules/three/package.json); the plugin enforces it. */
export const THREE_VERSION = '0.185.1';

/** Same-origin root of the decoder files; versioned so a new `three` is a new URL. */
export const DECODER_BASE = `/decoders/three-${THREE_VERSION}/`;

/** `DRACOLoader.setDecoderPath` directory (it appends the file names below). */
export const DRACO_DECODER_PATH = `${DECODER_BASE}draco/`;

/** `KTX2Loader.setTranscoderPath` directory (it appends the file names below). */
export const KTX2_TRANSCODER_PATH = `${DECODER_BASE}basis/`;

export interface DecoderFile {
  /** Path under `DECODER_BASE`, as the loaders request it. */
  readonly served: string;
  /** Path inside the installed `three` package. */
  readonly source: string;
}

/**
 * Every file the two loaders can request. DRACOLoader asks for the wrapper + wasm when
 * WebAssembly exists and for `draco_decoder.js` otherwise; it gets three's glTF-targeted build
 * (the one three recommends for `GLTFLoader`). KTX2Loader asks for the transcoder pair.
 */
export const DECODER_FILES: readonly DecoderFile[] = [
  {
    served: 'draco/draco_wasm_wrapper.js',
    source: 'examples/jsm/libs/draco/gltf/draco_wasm_wrapper.js',
  },
  { served: 'draco/draco_decoder.wasm', source: 'examples/jsm/libs/draco/gltf/draco_decoder.wasm' },
  { served: 'draco/draco_decoder.js', source: 'examples/jsm/libs/draco/gltf/draco_decoder.js' },
  { served: 'basis/basis_transcoder.js', source: 'examples/jsm/libs/basis/basis_transcoder.js' },
  {
    served: 'basis/basis_transcoder.wasm',
    source: 'examples/jsm/libs/basis/basis_transcoder.wasm',
  },
];
